import { useNavigate } from "react-router-dom";

import { Card } from "@components/cards/card";
import {
  ManagementActionEnums,
  ManagementTypeEnums,
  ManagementTypeTypes,
  hasManagementPermission,
} from "@hooks/dtos/user-dto";
import { ZuAuthStore } from "@hooks/stores/zustand-auth-store";
import {
  FaArrowRight,
  FaBoxOpen,
  FaCog,
  FaSatelliteDish,
  FaUsers,
} from "react-icons/fa";
import type { IconType } from "react-icons";

interface ManagementCard {
  title: string;
  description: string;
  icon: IconType;
  colorClassName: string;
  path?: string;
  permissionType?: ManagementTypeTypes;
}

const managementCards: ManagementCard[] = [
  {
    title: "Rastreios de Pedidos",
    description:
      "Acompanhe pedidos, consulte rastreios e visualize movimentações dos clientes.",
    icon: FaBoxOpen,
    colorClassName: "bg-primary-subtle text-primary",
    path: "/management/trackings",
    permissionType: ManagementTypeEnums.TRACKING,
  },
  {
    title: "Gerenciamento de Usuários",
    description:
      "Administre acessos, perfis e permissões dos usuários da plataforma.",
    icon: FaUsers,
    colorClassName: "bg-success-subtle text-success",
    path: "/management/users",
    permissionType: ManagementTypeEnums.CUSTOMER,
  },
  {
    title: "Webhook",
    description:
      "Monitore recebimentos, integrações e retornos de comunicação da plataforma.",
    icon: FaSatelliteDish,
    colorClassName: "bg-info-subtle text-info",
    path: "/management/webhooks",
    permissionType: ManagementTypeEnums.WEBHOOK,
  },
  {
    title: "Configurações",
    description:
      "Centralize parâmetros, preferências e regras operacionais da plataforma.",
    icon: FaCog,
    colorClassName: "bg-warning-subtle text-warning",
  },
];

export const Management = () => {
  const navigate = useNavigate();
  const { customer } = ZuAuthStore();
  const visibleManagementCards = managementCards.filter((item) => {
    if (!item.permissionType) return true;

    return hasManagementPermission(
      customer?.customer?.access,
      item.permissionType,
      ManagementActionEnums.VIEW,
    );
  });

  return (
    <>
      <div
        className="container-fluid d-flex flex-column justify-content-start align-items-center"
        style={{
          paddingBottom: "2.5rem",
          paddingTop: "clamp(1rem, 2vw, 2rem)",
          paddingLeft: "clamp(1rem, 5vw, 10rem)",
          paddingRight: "clamp(1rem, 5vw, 10rem)",
        }}
      >
        <div className="col-12">
          <Card className="shadow-sm rounded">
            {/* Card Header */}
            <div className="card-header">
              <div className="card-title">
                <div className="d-flex justify-content-between align-items-center">
                  <div>Gerenciamento da Plataforma</div>
                  <div>
                    <span
                      className={`rounded-circle d-inline-block me-2 bg-success`}
                      style={{ width: 15, height: 15 }}
                      title={`Ativo`}
                    ></span>
                  </div>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="card-body">
              <div className="row g-4">
                {visibleManagementCards.map((item) => {
                  const Icon = item.icon;
                  const isAvailable = Boolean(item.path);

                  return (
                    <div key={item.title} className="col-12 col-md-6 col-xl-4">
                      <Card className="h-100 border-0 shadow-sm">
                        <div className="card-body d-flex flex-column gap-3 p-4">
                          <div className="d-flex justify-content-between align-items-start gap-3">
                            <div
                              className={`rounded-3 d-inline-flex align-items-center justify-content-center p-3 ${item.colorClassName}`}
                            >
                              <Icon size={26} />
                            </div>

                            <span
                              className={`badge rounded-pill ${
                                isAvailable
                                  ? "text-bg-success"
                                  : "text-bg-secondary"
                              }`}
                            >
                              {isAvailable ? "Disponível" : "Em breve"}
                            </span>
                          </div>

                          <div>
                            <h5 className="card-title fw-bold mb-2">
                              {item.title}
                            </h5>
                            <p className="card-text text-muted mb-0">
                              {item.description}
                            </p>
                          </div>

                          <div className="mt-auto pt-2">
                            <button
                              type="button"
                              className={`btn w-100 d-flex align-items-center justify-content-center gap-2 ${
                                isAvailable
                                  ? "btn-primary"
                                  : "btn-outline-secondary"
                              }`}
                              disabled={!isAvailable}
                              onClick={() =>
                                item.path ? navigate(item.path) : undefined
                              }
                            >
                              {isAvailable ? "Acessar" : "Indisponível"}
                              {isAvailable && <FaArrowRight />}
                            </button>
                          </div>
                        </div>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};
